/**
 * Catalogos clinicos de colposcopia (terminologia IFCPC / Bethesda).
 *
 * Fuente unica de verdad: el backend valida contra estas listas y el frontend
 * construye los selectores a partir de ellas. Si una opcion no esta aqui, no
 * existe en el producto.
 *
 * Convencion: la ausencia de valor se representa con `undefined` o cadena vacia,
 * nunca con un centinela tipo "None" dentro del catalogo.
 */
/** Limite de caracteres para los campos de texto libre del estudio. */
export const MAX_TEXTO_LIBRE = 600;
/** Imagenes que admite un estudio. */
export const MAX_IMAGENES_COLPOSCOPIA = 12;
/** Minimo de caracteres de los hallazgos: menos no describe nada. */
export const MIN_HALLAZGOS = 3;
// --- Evaluación general ---------------------------------------------------
export const VALORACION_COLPOSCOPIA = ['Adecuada', 'No adecuada'];
export const CAUSAS_NO_ADECUADA = [
    'Sangrado',
    'Inflamación intensa',
    'Dolor',
    'Estenosis cervical',
    'Embarazo',
    'Artefactos o secreción abundante',
    'Otra',
];
export const ASPECTOS_CERVIX = [
    'Normal',
    'Ectropión',
    'Atrófico',
    'Estenótico',
    'Nulípara',
    'Multípara',
];
export const UNION_ESCAMOCOLUMNAR = [
    'Visible totalmente',
    'Visible parcialmente',
    'No visible',
];
export const ZONAS_TRANSFORMACION = [
    'Tipo 1 (completamente ectocervical)',
    'Tipo 2 (parcialmente endocervical)',
    'Tipo 3 (predominantemente endocervical)',
    'No identificable',
];
export const RESULTADOS_SCHILLER = [
    'No aplica',
    'Negativo (capta yodo)',
    'Positivo (no capta yodo)',
];
/** Escala de presencia usada en epitelio acetoblanco y estudios accesorios. */
export const PRESENCIA = ['No aplica', 'Ausente', 'Presente'];
/** Escala de resultado usada en vaginoscopia, vulvoscopia y anoscopia. */
export const RESULTADO_ACCESORIO = ['No evaluado', 'Negativo', 'Positivo'];
// --- Criterios por grado (IFCPC) ------------------------------------------
/**
 * Los cuatro ejes de valoracion. Cada eje se califica en tres grados:
 * menores, intermedios y mayores.
 */
export const CRITERIOS_IFCPC = {
    epitelio: {
        menores: [
            'Acetoblanco tenue',
            'Borde poco definido',
            'Superficie lisa',
            'Cambios finos puntiformes',
        ],
        intermedios: ['Acetoblanco moderado', 'Borde parcialmente nítido', 'Superficie irregular leve'],
        mayores: [
            'Acetoblanco denso',
            'Borde neto',
            'Superficie irregular marcada',
            'Signo del enrollamiento',
        ],
    },
    bordeSuperficie: {
        menores: ['Bordes mal definidos', 'Superficie homogénea'],
        intermedios: ['Bordes mixtos', 'Superficie mixta'],
        mayores: ['Bordes nítidos', 'Superficie heterogénea marcada', 'Lesión elevada o escamosa'],
    },
    angioarquitectura: {
        menores: ['Punteado fino', 'Mosaico fino'],
        intermedios: ['Punteado intermedio', 'Mosaico intermedio'],
        mayores: ['Punteado grueso', 'Mosaico grueso', 'Vasos atípicos'],
    },
    captacionYodo: {
        menores: ['Captación irregular', 'Captación parcial'],
        intermedios: ['Captación mixta'],
        mayores: ['No captación (Schiller positivo)', 'Área yodo-negativa extensa'],
    },
};
export const EJES_CRITERIOS = Object.keys(CRITERIOS_IFCPC);
export const GRADOS_CRITERIO = ['menores', 'intermedios', 'mayores'];
// --- Hallazgos misceláneos y diagnostico -----------------------------------
export const HALLAZGOS_MISCELANEOS = [
    'Cervicitis',
    'Ectopia',
    'Pólipo',
    'Leucoplasia',
    'Quistes de Naboth',
    'Condilomas',
    'Hemorragia',
    'Ulceración',
];
export const DIAGNOSTICOS_COLPOSCOPICOS = [
    'Normal',
    'Cambios inflamatorios',
    'Metaplasia escamosa',
    'LSIL (NIC 1)',
    'HSIL (NIC 2)',
    'HSIL (NIC 3)',
    'Sospecha de microinvasión',
    'Sospecha de invasión',
    'VPH sugestivo',
];
/** Todas las opciones validas de un eje, sin importar el grado. */
export function opcionesDelEje(eje) {
    const grados = CRITERIOS_IFCPC[eje];
    return [...grados.menores, ...grados.intermedios, ...grados.mayores];
}
/**
 * Grado al que pertenece una opcion dentro de un eje, o `undefined` si la
 * opcion no pertenece a ese eje. Sirve para calcular la severidad del estudio.
 */
export function gradoDeLaOpcion(eje, opcion) {
    for (const grado of GRADOS_CRITERIO) {
        if (CRITERIOS_IFCPC[eje][grado].includes(opcion))
            return grado;
    }
    return undefined;
}
// --- Captura del colposcopio (ADR-017) -------------------------------------
/**
 * Fase del examen en la que se tomo una captura. La elige quien opera: la
 * imagen no se modifica por elegirla (el «filtro verde» es el del colposcopio).
 */
export const FASES_DE_CAPTURA = [
    'sin-especificar',
    'sin-tincion',
    'solucion-salina',
    'acido-acetico',
    'lugol',
    'filtro-verde',
];
export const NOMBRE_DE_FASE = {
    'sin-especificar': 'Sin especificar',
    'sin-tincion': 'Sin tinción',
    'solucion-salina': 'Solución salina',
    'acido-acetico': 'Ácido acético',
    lugol: 'Lugol',
    'filtro-verde': 'Filtro verde',
};
/**
 * Etiquetas de la calidad de una captura. Describen la IMAGEN (foco,
 * exposicion, reflejos, encuadre), nunca el tejido.
 */
export const ETIQUETAS_DE_CALIDAD = [
    'buena',
    'aceptable',
    'regular',
    'baja',
    'sin-cervix',
];
export const NOMBRE_DE_CALIDAD = {
    buena: 'Buena',
    aceptable: 'Aceptable',
    regular: 'Regular',
    baja: 'Baja',
    'sin-cervix': 'Sin cérvix detectado',
};
/**
 * Escala nominal cuando el dispositivo no esta calibrado: un MARCADOR, no una
 * medida de ningun equipo. Todo valor que salga de ella se marca «nominal».
 */
export const PX_POR_MM_NOMINAL = 10;
export const MAX_MEDICIONES_POR_IMAGEN = 20;
export const MAX_VERTICES_POLIGONO = 64;
//# sourceMappingURL=colposcopia.js.map